import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  FileText,
  TrendingUp,
  Copy,
  Zap,
  Target,
  BarChart3,
  List,
} from 'lucide-react';
import { SpeechControls } from './SpeechControls';
import { formatTime } from '../utils/timeFormatter';

interface SummaryResponseDto {
  summary: string;
  originalLength: number;
  summaryLength: number;
  compressionRatio: number;
  level: 'ultra-court' | 'bullet-points' | 'detaillé';
  timestamp: Date;
  url?: string;
  fileName?: string;
  timeToReadOriginal?: number;
  timeToReadSummary?: number;
  timeSaved?: number;
  timeSavedPercentage?: number;
  efficiency?: string;
  timeSavedFormatted?: string;
  totalTimeSaved?: number;
  totalTimeSavedFormatted?: string;
  // Champs spécifiques à l'analyse d'image
  detectedType?: string;
  objectName?: string;
  confidence?: number;
  detailedPoints?: string[];
  detailedInfo?: Record<string, any>;
  wordCount?: number;
}

interface SummaryResultProps {
  result: SummaryResponseDto;
}

export const SummaryResult: React.FC<SummaryResultProps> = ({ result }) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  // Provide default values for missing properties
  const safeResult = {
    ...result,
    timeToReadOriginal: result.timeToReadOriginal || 0,
    timeToReadSummary: result.timeToReadSummary || 0,
    timeSaved: result.timeSaved || 0,
    timeSavedPercentage: result.timeSavedPercentage || 0,
    efficiency: result.efficiency || 'Correcte',
    timeSavedFormatted: result.timeSavedFormatted || '0s',
  };

  const getEfficiencyColor = (efficiency: string): string => {
    switch (efficiency) {
      case 'Excellente':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Très bonne':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Bonne':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Correcte':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Faible':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'ultra-court':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'bullet-points':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'detaillé':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getLevelLabel = (level: string): string => {
    switch (level) {
      case 'ultra-court':
        return 'Ultra-court';
      case 'bullet-points':
        return 'Points clés';
      case 'detaillé':
        return 'Détaillé';
      default:
        return level;
    }
  };

  // Fonction pour copier le résumé
  const copySummary = async () => {
    const summaryText = safeResult.summary;
    if (!summaryText || summaryText.trim() === '') {
      alert('Aucun résumé à copier.');
      return;
    }

    try {
      await navigator.clipboard.writeText(summaryText);
      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);

      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = summaryText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header avec niveau et efficacité */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('summary.result.title')}
        </h2>
        <div className="flex flex-wrap gap-2">
          <span
            className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getLevelColor(safeResult.level)}`}
          >
            {getLevelLabel(safeResult.level)}
          </span>
          <span
            className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getEfficiencyColor(safeResult.efficiency)}`}
          >
            {safeResult.efficiency}
          </span>
        </div>
      </div>

      {/* Case du résumé principal */}
      <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 shadow-sm">
        <div className="flex flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('summary.result.content')}
            </h3>
          </div>
          <div className="flex gap-2">
            <SpeechControls text={safeResult.summary} title="le résumé" />
            <button
              onClick={copySummary}
              className={`p-2 rounded-lg border transition-all duration-200 ${
                isCopied
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
              title={
                isCopied
                  ? t('common.copied', 'Copié !')
                  : t('summary.result.actions.copySummary')
              }
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="prose prose-gray max-w-none">
          <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-sm:text-justify">
            {safeResult.summary}
          </p>
        </div>
      </div>

      {/* Points détaillés pour l'analyse d'image */}
      {safeResult.detailedPoints && safeResult.detailedPoints.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <List className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
              Points détaillés d'analyse
            </h3>
          </div>
          <div className="space-y-2 sm:space-y-3">
            {safeResult.detailedPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                    {index + 1}
                  </span>
                </div>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                  {point}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Métriques de temps */}
      <div className="flex flex-row gap-2 sm:gap-3 lg:gap-4">
        {/* Temps de lecture original */}
        <div className="flex-1 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg sm:rounded-xl border border-red-200 dark:border-red-800 p-3 sm:p-4 lg:p-6 flex flex-col max-sm:justify-between">
          <div className="flex sm:flex-row items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-col max-sm:items-start">
            <div className="p-1.5 sm:p-2 bg-red-100 dark:bg-red-900/30 rounded-lg flex-shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[0.70rem] sm:text-sm font-medium text-red-700 dark:text-red-300">
                {t('summary.result.stats.originalReading')}
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-red-900 dark:text-red-100">
                {formatTime(safeResult.timeToReadOriginal)}
              </p>
            </div>
          </div>
          <p className="text-xs text-red-600 dark:text-red-400">
            {safeResult.originalLength.toLocaleString()} caractères
          </p>
        </div>

        {/* Temps de lecture résumé */}
        <div className="flex-1 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg sm:rounded-xl border border-green-200 dark:border-green-800 p-3 sm:p-4 lg:p-6 flex flex-col max-sm:justify-between">
          <div className="flex sm:flex-row  items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-col max-sm:items-start">
            <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex-shrink-0">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[0.70rem] sm:text-sm font-medium text-green-700 dark:text-green-300">
                {t('summary.result.stats.summaryReading')}
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-green-900 dark:text-green-100">
                {formatTime(safeResult.timeToReadSummary)}
              </p>
            </div>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400">
            {safeResult.summaryLength.toLocaleString()} caractères
          </p>
        </div>

        {/* Temps économisé */}
        <div className="flex-1 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg sm:rounded-xl border border-blue-200 dark:border-blue-800 p-3 sm:p-4 lg:p-6 flex flex-col max-sm:justify-between">
          <div className="flex sm:flex-row items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-col max-sm:items-start">
            <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[0.70rem] sm:text-sm font-medium text-blue-700 dark:text-blue-300">
                {t('summary.result.stats.timeSaved')}
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-blue-900 dark:text-blue-100">
                {safeResult.timeSavedFormatted}
              </p>
            </div>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            {safeResult.timeSavedPercentage}% d'efficacité
          </p>
        </div>
      </div>

      {/* Statistiques détaillées */}
      <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('trophies.categories.stats')}
          </h3>
        </div>
        <div className="flex gap-3 sm:gap-4 lg:gap-6">
          <div className="flex-1 text-center">
            <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1 sm:mb-2">
              {safeResult.compressionRatio.toFixed(1)}x
            </div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              {t('summary.result.stats.compression')}
            </p>
          </div>
          <div className="flex-1 text-center">
            <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-green-600 dark:text-green-400 mb-1 sm:mb-2">
              {safeResult.timeSavedPercentage}%
            </div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Temps économisé
            </p>
          </div>
          <div className="flex-1 text-center">
            <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1 sm:mb-2">
              {safeResult.efficiency}
            </div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              {t('summary.result.stats.efficiency')}
            </p>
          </div>
        </div>
      </div>

      {/* Informations contextuelles */}
      {(safeResult.url || safeResult.fileName) && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('summary.result.source')}
            </h3>
          </div>
          <div className="space-y-2 sm:space-y-3">
            {safeResult.url && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  URL :
                </span>
                <a
                  href={safeResult.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline break-all"
                >
                  {safeResult.url}
                </a>
              </div>
            )}
            {safeResult.fileName && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fichier :
                </span>
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-all">
                  {safeResult.fileName}
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('summary.result.generatedOn')} :
              </span>
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {new Date(safeResult.timestamp).toLocaleString(
                  t('common.locale', 'fr-FR')
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
